<script setup lang="ts">
import { TEMPLATES_CONFIG } from '@/views/autopilot/config/templates.config'

import type { MarketingFlowStep, TriggerSettings, DelaySettings, ActionSettings } from '../types/marketingFlow'
const { t } = useI18n()
const notification = useNotification()
const { frequencyTypesSegments, user } = useLocalStorage()
const { getMarketingFlowErrors, isUsingTemplate } = useMarketingFlow()

const props = defineProps<{
  modelValue: string | null
  currentStep: MarketingFlowStep
  lastUpdatedAt?: Date
  isCheckingMessageLength?: boolean
  isCreateMode?: boolean
  isSaving?: boolean
  isChanges?: boolean
  isAlreadyActive?: boolean
  triggerSettings?: TriggerSettings
  delaySettings?: DelaySettings
  actionSettings?: ActionSettings
}>()

const emit = defineEmits(['update:modelValue', 'onSave', 'onExit'])

const currentDay = new Date().toLocaleDateString().replaceAll('/', '-')
const scenarioNamePlaceholder = `${t('pages.marketingFlow.marketingFlowEditPage.myScenario')} ${currentDay}`

const marketingFlowNameFocused = ref(false)
const inputWidth = ref(300)
const hiddenSpan = ref<HTMLSpanElement | null>(null)

const marketingFlowErrors = computed(() => {
  return getMarketingFlowErrors(props.triggerSettings, props.delaySettings, props.actionSettings)
})

const hasErrors = computed(() => {
  return marketingFlowErrors.value.some((error) => error.severity === 'invalid')
})

const hasIncompleteErrors = computed(() => {
  return marketingFlowErrors.value.some((error) => error.severity === 'incomplete')
})

const stepStatus = computed(() => {
  if (hasErrors.value) {
    return 'invalid'
  }
  if (hasIncompleteErrors.value) {
    return 'incomplete'
  }
  if (props.isSaving) {
    return 'saving'
  }
  if (props.isChanges) {
    return 'modified'
  }
  return 'saved'
})

const extractVariablesFromMessage = (message: string): Record<string, string> | undefined => {
  const regex = /\{\{\s*(\w+)\s*\}\}/g
  const matches = message.matchAll(regex)
  const variables: Record<string, string> = {}

  for (const match of matches) {
    const variableName = match[1]
    variables[variableName] = `{{ ${variableName} }}`
  }

  return Object.keys(variables).length > 0 ? variables : undefined
}

const copyTemplateConfiguration = async () => {
  const smsAction = props.actionSettings?.actions?.find((action) => action.actionType === 'SEND_SMS')
  const messageContentKey = smsAction?.messageContent ? 'YOUR_MESSAGE_CONTENT_KEY' : undefined

  const messageContentVariables = smsAction?.messageContent
    ? extractVariablesFromMessage(smsAction.messageContent)
    : undefined

  const cleanedTriggerSettings = props.triggerSettings
    ? Object.fromEntries(
        Object.entries(props.triggerSettings).filter(
          ([key]) => key !== 'selectedStores' && key !== 'includedSegments' && key !== 'excludedSegments'
        )
      )
    : undefined

  const requiresSegmentType =
    frequencyTypesSegments.value.find((segment) => segment.id === props.triggerSettings.selectedAudience)
      ?.frequency_type || undefined

  const nextId = Math.max(...TEMPLATES_CONFIG.map((t) => t.id)) + 1
  const templateConfig = {
    id: nextId,
    type: 'camelCase type for template',
    title: 'i18n key for title',
    icon: 'IconoirGift',
    description: 'i18n key for description',
    details: [
      {
        subtitle: 'i18n key for subtitle',
        title: 'i18n key for title',
        desc_1: 'i18n key for desc_1',
        desc_2: 'i18n key for desc_2'
      },
      {
        title: 'i18n key for title',
        desc_1: 'i18n key for desc_1',
        desc_2: 'i18n key for desc_2'
      }
    ],
    disabled: false,
    time: 'estimated setup time in minutes',
    triggerSettings: cleanedTriggerSettings,
    delaySettings: props.delaySettings,
    actionSettings: props.actionSettings,
    ...(messageContentKey && { messageContentKey }),
    ...(messageContentVariables && Object.keys(messageContentVariables).length > 0 && { messageContentVariables }),
    ...(requiresSegmentType && { requiresSegmentType })
  }

  const formattedConfig = JSON.stringify(templateConfig, null, 2)
    .replace(/"([^"]+)":/g, '$1:')
    .replace(/: "([^"]*)"/g, ": '$1'")

  try {
    await navigator.clipboard.writeText(formattedConfig)
    notification.create('success', {
      text: 'Configuration copiée, ajoutez-la au fichier config/templates.config.ts',
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: 'Erreur lors de la copie',
      timeout: 2000
    })
  }
}

const showCopyConfig = computed(() => {
  return [6743, 8].includes(user.value.merchant.id)
})

const calculateWidth = () => {
  if (hiddenSpan.value) {
    const textWidth = hiddenSpan.value.offsetWidth
    inputWidth.value = Math.min(Math.max(textWidth + 40, 300), 550)
  }
}

watch(
  () => props.modelValue,
  async () => {
    await nextTick()
    calculateWidth()
  },
  { immediate: true }
)

onMounted(() => {
  if (!props.modelValue) {
    emit('update:modelValue', scenarioNamePlaceholder)
  }
  calculateWidth()
})
</script>

<template>
  <div class="flex h-[77px] w-full items-center border-b bg-white p-5 justify-between">
    <span ref="hiddenSpan" class="invisible absolute whitespace-nowrap font-semibold" style="pointer-events: none">
      {{ modelValue || scenarioNamePlaceholder }}
    </span>

    <div class="flex h-full items-center gap-3">
      <div
        class="flex h-full items-center rounded-lg bg-muted transition-all duration-200"
        :class="marketingFlowNameFocused ? 'ring-1 ring-primary' : ''"
        :style="{ width: `${inputWidth}px` }"
      >
        <input
          :value="modelValue"
          type="text"
          class="h-full min-h-3 flex-1 border-none bg-transparent font-semibold text-foreground outline-hidden! focus:ring-0 overflow-hidden text-ellipsis whitespace-nowrap pr-2"
          @focus="marketingFlowNameFocused = true"
          @blur="marketingFlowNameFocused = false"
          @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        />
        <BaseIcon name="HeroPencilIconOutline" class="mr-2 h-4 w-4 text-foreground" />
      </div>
      <slot name="after-name" />
    </div>

    <div class="flex items-center gap-3">
      <Button v-if="showCopyConfig" variant="outline" size="icon" @click="copyTemplateConfiguration">
        <BaseIcon name="HeroClipboardDocumentIconOutline" class="h-4 w-4" />
      </Button>

      <div class="flex gap-3 items-center">
        <div v-if="stepStatus === 'invalid'" class="flex h-10 flex-row items-center gap-2">
          <span class="text-foreground-secondary">{{
            t('pages.marketingFlow.marketingFlowEditPage.invalidForm')
          }}</span>
          <BaseIcon name="HeroExclamationTriangleIconOutline" class="h-6 w-6 text-red-400" />
        </div>
        <div v-if="stepStatus === 'incomplete'" class="flex h-10 flex-row items-center gap-2">
          <span class="text-foreground-secondary">{{
            t('pages.marketingFlow.marketingFlowEditPage.incompleteForm')
          }}</span>
          <BaseIcon name="HeroExclamationTriangleIconOutline" class="h-6 w-6 text-yellow-400" />
        </div>
        <div v-if="stepStatus === 'saving'" class="flex h-10 flex-row items-center gap-2">
          <span class="text-foreground-secondary">{{ t('pages.marketingFlow.marketingFlowEditPage.saving') }}</span>
          <Spinner />
        </div>
        <div v-if="stepStatus === 'modified' && !isCreateMode" class="flex h-10 flex-row items-center gap-2">
          <span class="text-foreground-secondary">{{
            t('pages.marketingFlow.marketingFlowEditPage.changesNotSaved')
          }}</span>
          <BaseIcon name="HeroExclamationCircleIconOutline" class="h-6 w-6 text-muted-foreground" />
        </div>
        <div v-if="stepStatus === 'modified' && isCreateMode" class="flex h-10 flex-row items-center gap-2">
          <span class="text-foreground-secondary">{{ t('pages.marketingFlow.marketingFlowEditPage.valid') }}</span>
          <BaseIcon name="HeroCheckCircleIconOutline" class="h-6 w-6 text-green-500" />
        </div>

        <AppTooltip
          v-if="stepStatus === 'saved' && lastUpdatedAt"
          tooltip-position="left"
          :text="
            t('pages.marketingFlow.marketingFlowEditPage.lastUpdated', {
              date: lastUpdatedAt.toLocaleDateString(),
              time: lastUpdatedAt.toLocaleTimeString()
            })
          "
        >
          <div class="flex h-[40px] flex-row items-center gap-2">
            <span class="text-foreground-secondary">{{ t('pages.marketingFlow.marketingFlowEditPage.saved') }}</span>
            <BaseIcon name="HeroCheckCircleIconOutline" class="h-6 w-6 text-green-500" />
          </div>
        </AppTooltip>

        <Button variant="outline" @click="emit('onExit')">
          {{ t('pages.marketingFlow.marketingFlowEditPage.exit') }}
        </Button>
        <Button
          variant="default-primary"
          :disabled="(!isChanges && !isUsingTemplate) || isCheckingMessageLength || hasErrors || hasIncompleteErrors"
          @click="emit('onSave')"
        >
          {{ t('pages.marketingFlow.marketingFlowEditPage.editScenario') }}
        </Button>
      </div>
    </div>
  </div>
</template>
